import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import DiscardIcon from '@material-ui/icons/DeleteSweep';
import SearchBar from 'tuc-webui-base/components/SearchBar';
import { extendViewService } from 'tuc-webui-base/services/ViewService';
import { useRowNumber, usePendingSelection } from 'tuc-webui-base/components/TableViewComponent';
import ConfirmDialog from 'tuc-webui-base/components/ConfirmDialog';
import MessageDialog from 'grb-webui/components/MessageDialog';
import FilterBar, { withPageFilter } from 'grb-webui/components/FilterBar';
import { TABLE_PANEL_TYPE } from 'grb-webui/ComponentTypes';
import { withStoreData } from 'grb-webui/store';
import AppMainPage, { FIT_TO_PAGE_VARIANT } from '../../components/AppMainPage';
import {
  BATCHES_ENTITY,
} from '../../Entities';
import { getBatchJobId } from '../../services/BatchService';
import BatchFilter from './BatchFilter';
import BatchButtons from './BatchButtons';
import BatchStatus from './BatchStatus';
import BatchObjectList from './BatchObjectList';
import './BatchesContainer.scss';

const DEFAULT_OPTIONS = {
  useDiscard: true,
  useRetry: true,
};

export default (opts = {}) => {
  const options = { ...DEFAULT_OPTIONS, ...opts };
  const { useDiscard, useRetry } = options;
  const ViewServiceProperties = {
    componentsByName: {
      BatchButtons: BatchButtons(options),
      BatchStatus,
      BatchObjectList,
    },
    editors: [],
    iconsByName: {
      DiscardBatch: DiscardIcon,
    },
  };

  const tableProps = useDiscard
    ? { getRowClassName: key => classnames('grb-batch-row', key.discarded && 'grb-discarded-batch-row') }
    : {};

  const BatchesContainer = (props) => {
    const {
      filter,
      arrayFilter,
      onFilterChange,
      filterEditor,
      onUpdateBatches,
      batches,
      search,
      onSearchChange,
      configuration,
      ...rest
    } = props;
    const {
      viewService,
      batchService,
      onSnackbarMessage,
      location,
      history,
    } = props;
    const [dialogMessage, setDialogMessage] = useState({ open: false });
    const [confirmDialog, setConfirmDialog] = useState({ open: false });
    const [TablePanel] = useState(() => viewService.getComponent(TABLE_PANEL_TYPE));
    const [extViewService] = useState(() => extendViewService(viewService, ViewServiceProperties));
    const batchesCount = useRowNumber(batches, false, true /* Server filtering */);
    const [tableModel] = useState(() => {
      const validateDiscardBatch = (batch) => {
        return batchService.validateDiscardBatch(batch);
      };

      const handleDiscardBatches = (chkBatches) => {
        batchService.discardBatches(chkBatches)
        .then(() => {
          onSnackbarMessage({ message: `${chkBatches.length === 1 ? 'Batch' : 'Batches'} discarded` });
        })
        .catch(e => viewService.onError(e));
      };

      return useDiscard ? {
        canDelete: true,
        canCheck: true,
        onDeleteObject: handleDiscardBatches,
        validateDelete: validateDiscardBatch,
      } : undefined;
    });

    //
    // Row selection
    //
    const submittedBatchIdForTest = location.state && location.state.selectedBatchId;
    const { selected, onSelect } = usePendingSelection(
      submittedBatchIdForTest,
      batches,
    );

    //
    // Abort batch
    //
    const handleCloseMessageDialog = useCallback(() => {
      setDialogMessage({ open: false });
    }, []);

    const handleAbortBatch = useCallback((batch) => {
      const abortBatch = (b) => {
        setConfirmDialog({ open: false });
        if (b) {
          batchService.abortBatch(b)
          .then(() => {
            onSnackbarMessage({ message: 'Batch aborted' });
          })
          .catch(e => viewService.onError(e));
        }
      };

      const message = batchService.validateAbort(batch);
      if ((message !== true) && (message !== null)) {
        setDialogMessage({ title: 'Abort Batch Warning', message, open: true });
      } else {
        setConfirmDialog({
          open: true,
          title: 'Confirm Abort',
          message: 'Are you sure you want to abort this batch?',
          buttons: [
            { title: 'Cancel', value: false, id: 'grb-cancel-abort' },
            { title: 'Abort', value: batch, id: 'grb-confirm-abort' },
          ],
          onClose: abortBatch,
        });
      }
    }, [batchService]);

    //
    // Retry batch
    //
    const handleRetryBatch = useCallback((batch) => {
      const msg = batchService.validateRetry(batch);
      if ((msg !== true) && (msg !== null)) {
        setDialogMessage({ title: 'Retry Batch Warning', message: msg, open: true });
      } else {
        batchService.retryBatch(batch)
          .catch(e => viewService.onError(e));
      }
    }, [batchService]);

    //
    // Job log
    //
    const handleLogBatch = useCallback((batch) => {
      const jobId = getBatchJobId(batch);
      if (jobId) {
        history.push(`/jobs/log?id=${jobId}`, { referrer: location });
      }
    }, [batchService, history]);

    const filteredBatches = arrayFilter ? arrayFilter(batches) : batches;
    //
    // Header
    //
    const HeaderBar = (
      <React.Fragment>
        <FilterBar
          {...rest}
          filter={filter || {}}
          filterEditor={filterEditor}
          onFilterChange={onFilterChange}
        />
        <SearchBar
          {...props}
          placeholder="Search batches table..."
          value={search}
          onChange={onSearchChange}
        />
      </React.Fragment>
    );

    return (
      <AppMainPage
        route={props.route}
        variant={FIT_TO_PAGE_VARIANT}
        TrailingBar={HeaderBar}
        className="grb-batches-page"
        rowCount={batchesCount}
        help={configuration && configuration.help}
      >
        {submittedBatchIdForTest && (
          <div style={{ display: 'none' }} id="submittedBatchIdForTest" className={submittedBatchIdForTest} />
        )}
        <div className={classnames('grb-batches-table-panel', 'grb-fullpage', 'MuiPaper-elevation4')}>
          <TablePanel
            {...rest}
            {...rest.component}
            {...tableProps}
            batches={filteredBatches}
            viewService={extViewService}
            batchesCount={batchesCount}
            tableModel={tableModel} /* For discarding several batches from the table */
            onRetryBatch={useRetry ? handleRetryBatch : undefined}
            onAbortBatch={handleAbortBatch}
            onLogBatch={handleLogBatch}
            selected={selected}
            onSelect={onSelect}
          />
          {dialogMessage.open && (<MessageDialog {...dialogMessage} onClose={handleCloseMessageDialog} />)}
          {confirmDialog.open && (<ConfirmDialog {...confirmDialog} maxWidth="md" />)}
        </div>
      </AppMainPage>
    );
  };

  BatchesContainer.propTypes = {
    batches: PropTypes.arrayOf(PropTypes.shape({})),
    viewService: PropTypes.shape({
      onError: PropTypes.func,
      getComponent: PropTypes.func,
    }).isRequired,
    route: PropTypes.shape({}).isRequired,
    location: PropTypes.shape({
      state: PropTypes.shape({
        selectedBatchId: PropTypes.string,
      }),
    }).isRequired,
    history: PropTypes.shape({
      push: PropTypes.func,
    }).isRequired,
    query: PropTypes.shape({
      batchId: PropTypes.string,
    }),
    batchService: PropTypes.shape({
      validateAbort: PropTypes.func,
      abortBatch: PropTypes.func,
      validateRetry: PropTypes.func,
      retryBatch: PropTypes.func,
      validateDiscardBatch: PropTypes.func,
      discardBatches: PropTypes.func,
    }).isRequired,
    component: PropTypes.shape({}).isRequired,
    arrayFilter: PropTypes.func,
    onFilterChange: PropTypes.func,
    filter: PropTypes.shape({}),
    filterEditor: PropTypes.shape({}),
    onSnackbarMessage: PropTypes.func.isRequired,
    onUpdateBatches: PropTypes.func,
    search: PropTypes.string,
    onSearchChange: PropTypes.func,
    configuration: PropTypes.shape({
      help: PropTypes.shape({}),
    }),
  };

  BatchesContainer.defaultProps = {
    batches: undefined,
    filter: undefined,
    arrayFilter: undefined,
    onFilterChange: undefined,
    filterEditor: undefined,
    onUpdateBatches: undefined,
    query: {},
    search: undefined,
    onSearchChange: undefined,
    configuration: undefined,
  };

  return withPageFilter(
    withStoreData(BatchesContainer, {
      entities: [{ name: BATCHES_ENTITY, getQuery: (pps) => pps.serverQuery }],
      onUpdateStore: 'onUpdateBatches',
    }),
    BatchFilter(options),
  );
};
